import { Box, Loader, Spacer, Text } from '@jfs/ui-new'
import { getErrorMessage, getLoadingState } from '@jfs/utils'
import { useNavigation, useRoute } from '@react-navigation/native'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import ActionsMenu from '../../comps/ActionMenu/ActionsMenu'
import EmptyView from '../../comps/EmptyView'
import { ModBox } from '../../comps/ModifiedBox/ModifiedBox'
import { NewTable } from '../../comps/NewTable'
import { TimeOut, Toast, ToastPosition, ToastType } from '../../comps/Toast'
import { settlementFileOptionsExport, settlementTitleDataV2 } from '../../data/static/data'
import { headerTitle, settlementScreenString, staticStrings } from '../../data/static/strings'
import { useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { ActionType, getCustomColumnWidth, getWidth, isMobile, tableConfig, useWindowDimensions } from '../../util/util'
import styles from './styles'

const SettlementsScreen: FC = () => {
  const themes = styles(0)

  const {
    fetchSettlementSummary,
    selectedDateInfo,
    updateSelectedDate,
    mid,
    generateSettlementFile,
    settlementFileDownload,
    onSettlementRetry,
    getSettlementScreenData,
    fetchSettlementsBasedOnPath,
    // pageNumber,
    isFieldsNotEmpty,
    setDownloadReportType,
    toastMessage,
    toastResetMessage,
    searchSettlementById,
    settlementDeleteFile,
  } = useStores((store) => ({
    fetchSettlementSummary: store.settlements.fetchSettlementSummary,
    selectedDateInfo: store.settlements.selectedDateInfo,
    updateSelectedDate: store.settlements.updateSelectedDateInfo,
    mid: store.user.selectedMid,
    generateSettlementFile: store.settlements.generateSettlementFile,
    settlementFileDownload: store.settlements.settlementFileDownload,
    onSettlementRetry: store.onSettlementRetry,
    getSettlementScreenData: store.getSettlementScreenData,
    fetchSettlementsBasedOnPath: store.fetchSettlementsBasedOnPath,
    // pageNumber: store.settlements.pagination.number,
    isFieldsNotEmpty: store.transactions.isFieldsNotEmpty,
    settlementListLength: store.settlements.settlement.length, //Fix for Settlement Generating.. row not showing up
    setDownloadReportType: store.settings.metaData.setDownloadReportType,
    toastMessage: store?.reports?.getMessage(),
    toastResetMessage: store?.reports?.resetMessage,
    searchSettlementById: store.settlements.searchSettlementById,
    settlementDeleteFile: store.settlements.deleteSettlement,
  }))

  const viewPortWidth = useWindowDimensions()
  const navigate = useNavigation().navigate

  const smallDimension = viewPortWidth < 416
  const isSmallDevice = isMobile(!smallDimension)

  const path = useRoute().name
  const listData = getSettlementScreenData()
  const [fieldSettingVisible, setFieldSettingVisible] = useState(false)
  const [filterVisible, setFilterVisible] = useState(false)

  const [fetchSettlements, fetchStmtHandler] = useLazyApiData(fetchSettlementSummary)
  const [generateSettlemFile, generateFileHandler] = useLazyApiData(generateSettlementFile)
  const [setlmentFileDwnld, settlementFileDwnldHandler] = useLazyApiData(settlementFileDownload)
  const [settlementDeleteFileApi, settlementDeleteFileHandler] = useLazyApiData(settlementDeleteFile)

  const isSettlementReportTemplateAvailable = true

  const apis = [fetchStmtHandler]

  const abortControllerRef = useRef<AbortController>()
  const fetchDetailOfPath = () => {
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    fetchSettlementsBasedOnPath(fetchSettlements, abortControllerRef.current)
  }

  const fetchDetailsOfPath = useCallback(fetchDetailOfPath, [fetchSettlements])

  useEffect(() => {
    if (settlementDeleteFileHandler.state === 'fulfilled') {
      fetchDetailsOfPath()
    }
  }, [settlementDeleteFileHandler.state, fetchDetailsOfPath])

  useEffect(() => {
    fetchDetailsOfPath()
  }, [path, mid, fetchDetailsOfPath, isFieldsNotEmpty])

  const handleRetry = (): void => {
    onSettlementRetry(fetchSettlements)
  }

  const loadingState = getLoadingState(apis)
  const errorState = getErrorMessage(apis)
  const emptyState = listData.length > 0 && !loadingState

  const getTitle = (): string => {
    return headerTitle[tableConfig(path).status]
  }

  const getEmptyMessage = (): string => {
    return listData.length === 0 ? settlementScreenString.noRecords : settlementScreenString.empty
  }

  const onItemAction = (actionType: ActionType, id: string) => {
    const filtereData = listData.filter((item: any) => item.id === id)
    const data = filtereData[0]
    const actionItemEventObj = {
      [ActionType.DownloadSettlementCSV]: () => {
        const files = data?.fileName
        setlmentFileDwnld(mid, data?.id, files, setlmentFileDwnld, 'TXT', data?.fromDate, data?.toDate) // for loader to work sending callback to use in extend session scenario
      },
    }
    if (actionItemEventObj[actionType as keyof typeof actionItemEventObj]) {
      actionItemEventObj[actionType as keyof typeof actionItemEventObj]()
    }
  }

  const pageTitle = getTitle()

  const scrollViewStyle = () => {
    if (listData.length === 0) {
      return themes[staticStrings.settlements].emptyContainer
    }
    return themes[staticStrings.settlements].tableContainer(loadingState)
  }

  const onDownloadFileAction = (fileType: string, id: string) => {
    const data = searchSettlementById(id)
    const files = data?.fileName
    const fileExt = settlementFileOptionsExport.find((item) => item.title === fileType)?.type
    //console.log('data?.fromDate, data?.toDate', data?.fromDate, data?.toDate)
    setlmentFileDwnld(mid, data?.id, files, setlmentFileDwnld, fileExt, data?.fromDate, data?.toDate) // for loader to work sending callback to use in extend session scenario
    // actionItemEventObj[actionType] && actionItemEventObj[actionType]()
  }

  const onDeleteReportFile = (data: any) => {
    settlementDeleteFileApi(data, mid)
  }

  const scrollStyle = scrollViewStyle()

  const handleToastAction = () => {
    setDownloadReportType('Settlement')
    toastResetMessage()
    navigate(Routes.DOWNLOADS as never)
  }
  const handleToastDismiss = () => {
    toastResetMessage()
  }

  const onReportTemplatesPress = () => {
    navigate(isSettlementReportTemplateAvailable ? Routes.SETTLEMENTS_REPORTING : Routes.REPORT_CONFIGURATION)
  }

  return (
    <>
      <Box f={1} overflowY={'hidden'}>
        <Box {...themes.maincontainer}>
          <Toast
            message={toastMessage}
            hide={toastMessage.message === ''}
            position={ToastPosition.TOP_POSITIVE_ACTION_VIEW}
            type={ToastType.POSITIVE_ACTION_VIEW}
            onDismiss={handleToastDismiss}
            onAction={handleToastAction}
            actionLabel={'Go To Downloads'}
            timeOut={TimeOut.FIVE_SECONDS}
          />
          <Box {...themes[path].container}>
            <Box {...themes.stickyHeader}>
              <Text testID={'pageTitle'} type={'h0Bold'}>
                {pageTitle}
              </Text>
              <ActionsMenu
                path={'settlements'} //Should be settlements as its used to map pagination in actionsmenu
                generateSettlementFile={generateSettlemFile}
                fetchDetailOfPath={fetchDetailOfPath}
                fieldSettingVisible={fieldSettingVisible}
                setFieldSettingVisible={setFieldSettingVisible}
                filterVisible={filterVisible}
                setFilterVisible={setFilterVisible}
                generateFileHandler={generateFileHandler}
                selectedDateInfo={selectedDateInfo}
                updateSelectedDate={updateSelectedDate}
                onReportTemplatesPress={onReportTemplatesPress}
                maxRange={31}
              />
            </Box>
            <ModBox
              show={
                settlementFileDwnldHandler?.state === 'pending' ||
                generateFileHandler?.state === 'pending' ||
                settlementDeleteFileHandler?.state === 'pending'
              }>
              <Loader color='BLUE'/>
            </ModBox>
            <EmptyView
              onRetry={handleRetry}
              isLoading={loadingState}
              error={errorState}
              emptyMessage={getEmptyMessage()}
              loadingMessage={settlementScreenString.loading}
              hide={emptyState}
              loaderColor={'BLUE'}
            />
            <Spacer size={5} direction="vertical" />
            <Box {...scrollStyle} nativeID={'scroll-horizontal-Table'} f={1}>
              <NewTable
                data={listData}
                rowHeight={48}
                headerHeight={64}
                onAction={onItemAction}
                customWidthFunc={getCustomColumnWidth}
                freezeColumn="ERN"
                tableWidth={getWidth(viewPortWidth > 1200 ? viewPortWidth - 290 : viewPortWidth, settlementTitleDataV2)}
                headerRowKeys={settlementTitleDataV2}
                onDownloadFileAction={onDownloadFileAction}
                onDeleteReportFile={onDeleteReportFile}
                onRowClick={() => null}
                hide={filterVisible || fieldSettingVisible || !(!loadingState && !errorState && !getEmptyMessage())}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <ModBox {...themes.settlmentNote} show={!isSmallDevice} marginTop={10}>
        <Text type={'footnote'}>{staticStrings.settlementNote}</Text>
      </ModBox>
    </>
  )
}

export default SettlementsScreen
